import { useQuery } from '@tanstack/react-query';
import { PunchType } from '@construction/shared';
import { attendanceApi, type CurrentShift } from '@/api/endpoints';
import { resolveShiftState, type LocalPunch } from '@/lib/shift-state';
import { useAuthStore } from '@/store/auth';

/**
 * 工地锁定（CR-015）。
 *
 * 工人打了上班卡就锁在那个工地上，图纸、进度表、日报、隐蔽工程全部跟着锁，
 * 直到打下班卡。采购不锁 —— 一次采购可能同时为几个工地买料。
 *
 * **锁定的真相在服务端**：它是「我有没有一条未闭合的上班卡」，只有服务端知道。
 * 但打卡是先入队再上传的，按下按钮那一刻服务端还不知道 —— 所以本地要记下
 * 刚打的那张卡，在服务端追上来之前由它说了算（合并逻辑和用例见 `lib/shift-state`）。
 */

const LOCAL_SHIFT_KEY = 'construction.openShift';

export type { LocalPunch };

export interface SiteLock {
  /** 锁在哪个工地。null = 没在班，可以自由切工地 */
  lockedSiteId: number | null;
  /** 正在上班。**已经把队列里那张卡算进去了** */
  open: boolean;
  /** 班次开始时刻（ISO），计时器用 */
  startedAt: string | null;
  /** 这个状态还没被服务端确认，那张卡还在队列里 */
  pendingSync: boolean;
  /** 服务端原始返回。`site`（人被移出工地后仍要能闭上这个班）和 `stale` 从这里取 */
  shift: CurrentShift | null;
  /**
   * 服务端时间与本机的差（毫秒），**在收到响应那一刻量一次**。
   *
   * 不能在渲染时现算：`Date.now() - Date.parse(serverNow)` 里 `serverNow` 是
   * 上次请求时冻结的，而 `Date.now()` 每次渲染都在走 —— 两边一起增长，
   * 计时器减掉它之后**永远停在请求那一刻的数字**。客户报的「已工作时间不会变」
   * 就是这么来的。
   */
  clockSkewMs: number;
  isLoading: boolean;
}

export function useSiteLock(): SiteLock {
  const userId = useAuthStore((s) => s.user?.id);

  const query = useQuery({
    queryKey: ['me-shift'],
    queryFn: async () => {
      const shift = await attendanceApi.currentShift();
      // 时钟差必须和响应一起存进缓存，理由见 clockSkewMs 的注释
      return { ...shift, skewMs: Date.now() - Date.parse(shift.serverNow) };
    },
    enabled: userId !== undefined,
    staleTime: 30_000,
    // 回到前台时刷一次：这是发现「管理员已经把我强制下班了」的最好时机
    refetchOnWindowFocus: true,
    refetchInterval: 60_000,
    // 工地手机一天要撑十小时，切到后台就别轮询了
    refetchIntervalInBackground: false,
  });

  const shift = query.data ?? null;
  const local = userId !== undefined ? readLocalPunch(userId) : null;

  const resolved = resolveShiftState(
    shift ? { open: shift.open, punch: shift.punch } : null,
    local,
  );

  // 服务端已经收到那张卡了，本地这份没用了 —— 留着只会在下次离线时
  // 拿一条陈年记录去覆盖服务端状态
  if (resolved.staleLocal && userId !== undefined) clearLocalPunch(userId);

  return {
    lockedSiteId: resolved.lockedSiteId,
    open: resolved.open,
    startedAt: resolved.startedAt,
    pendingSync: resolved.pendingSync,
    shift,
    clockSkewMs: shift?.skewMs ?? 0,
    isLoading: query.isLoading,
  };
}

/**
 * 本地记下最后一次打卡：它还在队列里时，界面状态只能靠它。
 *
 * 按 userId 分键 —— 手机多人轮换使用，上一个人的班次不能锁住下一个人。
 */
export function writeLocalPunch(userId: number, punch: LocalPunch): void {
  try {
    localStorage.setItem(`${LOCAL_SHIFT_KEY}.${userId}`, JSON.stringify(punch));
  } catch {
    /* 隐私模式下存不下，退化成只靠服务端 */
  }
}

export function readLocalPunch(userId: number): LocalPunch | null {
  try {
    const raw = localStorage.getItem(`${LOCAL_SHIFT_KEY}.${userId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as LocalPunch;
    // CR-019 之前存的记录里 siteId 是 cuid 字符串。它和整数 id 永远比不相等，
    // 结果是锁定状态挂着但哪个工地都对不上 —— 直接当作废记录清掉。
    if (typeof parsed.siteId !== 'number') {
      clearLocalPunch(userId);
      return null;
    }
    // 超过一天的本地记录一律作废：多半是那条打卡被拒了，或者人早就下班了
    if (Date.now() - Date.parse(parsed.occurredAt) > 24 * 60 * 60 * 1000) {
      clearLocalPunch(userId);
      return null;
    }
    // 老记录没有 type，那时候只记上班卡
    return { ...parsed, type: parsed.type ?? PunchType.IN };
  } catch {
    return null;
  }
}

export function clearLocalPunch(userId: number): void {
  try {
    localStorage.removeItem(`${LOCAL_SHIFT_KEY}.${userId}`);
  } catch {
    /* 忽略 */
  }
}
